import { DATAVIZ_SERIES } from './palette';

/**
 * The seam every data-expression component paints through (§ 311).
 *
 * **Why the classes are written out.** Tailwind's scanner reads source as TEXT: it
 * matches literal class names and nothing else. `` `tw-text-dataviz-${n}` `` produces
 * a class at runtime that was never emitted at build time, so the element renders
 * with no colour at all — and `tsc` is clean, jsdom sees the class in `className`,
 * and every test passes. That failure shape has already cost this repo real debugging
 * time twice (the Skeleton width classes, the `motion-safe:tw-transition-[a,b]`
 * arbitrary value), so the list is spelled out and pinned by `dataviz.test.ts`.
 *
 * **Colour arrives as `currentColor`.** These are `text-` classes, not `fill-` or
 * `stroke-`, so one class on a wrapper colours every mark inside it — the
 * `Iconography` arrangement. It is also what makes theming free: the class resolves
 * `var(--ds-dataviz-N)`, and `tokens.css` re-points that variable under
 * `[data-theme="dark"]`.
 */
const SERIES_TEXT: readonly string[] = [
  'tw-text-dataviz-1',
  'tw-text-dataviz-2',
  'tw-text-dataviz-3',
  'tw-text-dataviz-4',
  'tw-text-dataviz-5',
  'tw-text-dataviz-6',
];

/**
 * A series index → its ink class.
 *
 * **Wraps rather than clamping.** A chart handed seven categories must not paint the
 * seventh in the same colour as the sixth, which is what clamping would do — two
 * adjacent slices becoming one shape is a worse failure than a repeat that is at
 * least six apart. Negative and non-integer indices are normalised rather than
 * rejected, because the caller is usually an array index or a modulo and a thrown
 * error in a table cell takes the whole page down.
 */
export const seriesClass = (index: number): string => {
  const n = Number.isFinite(index) ? Math.trunc(index) : 0;
  const wrapped = ((n % SERIES_TEXT.length) + SERIES_TEXT.length) % SERIES_TEXT.length;
  return SERIES_TEXT[wrapped];
};

/** The number of distinct series before `seriesClass` starts repeating. */
export const SERIES_COUNT = SERIES_TEXT.length;

/* The seam and the roles cannot drift: one list is the classes, the other the roles
   they resolve. `dataviz.test.ts` asserts the two stay the same length and order. */
export { DATAVIZ_SERIES };
